<!-- eslint-disable -->
<template>
  <div
    class="crm-column"
    :class="[
      { 'crm-column--collapsed': isCollapsed },
      { 'crm-column--wip-exceeded': isWipExceeded }
    ]"
  >
    <!-- Column Header -->
    <div class="crm-column__header">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center flex-grow-1 min-w-0 mr-2">
          <span
            class="crm-column__dot"
            :style="{ backgroundColor: stage.color || '#cbd5e1' }"
          />
          <h3 class="crm-column__title text-truncate" :title="stage.name">{{ stage.name }}</h3>
        </div>

        <div class="d-flex align-items-center">
          <!-- Card Count / WIP limit -->
          <span
            class="crm-column__count mr-1"
            :class="{ 'crm-column__count--warning': isWipExceeded }"
            :title="stage.wipLimit > 0 ? `WIP Limit: ${cards.length}/${stage.wipLimit}` : `${displayTotal} items`"
          >
            {{ hasMore ? `${cards.length}/${displayTotal}` : displayTotal }}{{ stage.wipLimit > 0 ? `/${stage.wipLimit}` : '' }}
          </span>

          <!-- Collapse toggle button -->
          <button
            type="button"
            class="crm-column__toggle"
            @click="isCollapsed = !isCollapsed"
            :title="isCollapsed ? 'Expand column' : 'Collapse column'"
          >
            <feather-icon :icon="isCollapsed ? 'ChevronDownIcon' : 'ChevronUpIcon'" size="14" />
          </button>
        </div>
      </div>

      <!-- Summed Revenue for Deals & Renewals -->
      <!--
        A column total is only meaningful in ONE currency. The price list returns SAR
        for KSA schemes and AED for others, so summing them and stamping "AED" on the
        result was arithmetic nonsense. Totals are now per currency, and a column
        holding more than one says so instead of inventing a single number.
      -->
      <div v-if="!isCollapsed && activePipeline !== 'tickets'" class="crm-column__sub">
        <span v-for="(sum, code) in totalsByCurrency" :key="code" class="mr-1">
          {{ code }} {{ formatCurrency(sum) }}
        </span>
        <span v-if="unpricedCount" class="crm-column__unpriced" :title="unpricedCount + ' card(s) have no price yet'">
          · {{ unpricedCount }} unpriced
        </span>
      </div>
    </div>

    <!-- Column Body: vuedraggable Card List -->
    <div v-if="!isCollapsed" class="crm-column__cards scrollable-y">
      <draggable
        :list="cards"
        :group="group"
        ghost-class="crm-card--ghost"
        drag-class="crm-card--dragging"
        :animation="200"
        handle=".crm-card"
        @change="handleDragChange"
      >
        <kanban-card
          v-for="card in cards"
          :key="card.id"
          :card="card"
          :active-pipeline="activePipeline"
          :board-settings="boardSettings"
          :bulk-mode="bulkMode"
          :is-selected="isCardSelected(card)"
          @click="$emit('card-click', $event)"
          @quick-action="$emit('card-quick-action', $event)"
          @select="$emit('card-select', $event)"
        />
      </draggable>

      <!-- Empty Column Placeholder / Drop Zone -->
      <div v-if="!cards.length" class="crm-column__empty">
        <feather-icon icon="FolderMinusIcon" size="24" class="text-muted mb-1" />
        <span>Empty Stage</span>
        <span class="text-xs text-muted">Drag cards here</span>
      </div>

      <!-- Audit A-1: never hide cards silently — show the window + load more -->
      <div v-if="hasMore" class="crm-column__more">
        <span class="crm-column__more-label">Showing {{ cards.length }} of {{ displayTotal }}</span>
        <button
          type="button"
          class="crm-column__load-more"
          :disabled="loadingMore"
          @click="$emit('load-more', stage.id)"
        >
          {{ loadingMore ? 'Loading…' : 'Load more' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import draggable from 'vuedraggable'
import KanbanCard from './KanbanCard.vue'

export default {
  name: 'KanbanColumn',
  components: {
    draggable,
    KanbanCard
  },
  props: {
    stage: { type: Object, required: true },
    cards: { type: Array, required: true },
    activePipeline: { type: String, required: true },
    boardSettings: { type: Object, required: true },
    bulkMode: { type: Boolean, default: false },
    selectedCardIds: { type: Array, default: () => [] },
    group: { type: String, default: 'pipeline-cards' },
    // Audit A-1: server-side total for this stage (null = unknown/all loaded)
    totalCount: { type: Number, default: null },
    loadingMore: { type: Boolean, default: false }
  },
  data() {
    return {
      isCollapsed: false
    }
  },
  computed: {
    /** Sum per currency — never across currencies. */
    totalsByCurrency() {
      const out = {}
      this.cards.forEach(c => {
        const v = Number(c.value) || 0
        if (!v) return
        const code = c.currency || 'AED'
        out[code] = (out[code] || 0) + v
      })
      return out
    },
    /** Cards with no price yet — honest, and a prompt to go and price them. */
    unpricedCount() {
      return this.cards.filter(c => !Number(c.value)).length
    },
    isWipExceeded() {
      return this.stage.wipLimit > 0 && this.cards.length > this.stage.wipLimit
    },
    displayTotal() {
      return this.totalCount !== null && this.totalCount > this.cards.length
        ? this.totalCount
        : this.cards.length
    },
    hasMore() {
      return this.totalCount !== null && this.totalCount > this.cards.length
    }
  },
  methods: {
    isCardSelected(card) {
      return this.selectedCardIds.includes(card.id)
    },
    handleDragChange(evt) {
      if (evt.added) {
        this.$emit('card-moved', {
          card: evt.added.element,
          toStage: this.stage.id,
          newIndex: evt.added.newIndex
        })
      }
    },
    formatCurrency(val) {
      if (!val) return '0'
      return Number(val).toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
$slate-100: #f1f5f9;
$slate-200: #e2e8f0;
$slate-400: #94a3b8;
$slate-500: #64748b;
$slate-700: #334155;
$danger: #f43f5e;
$warning: #2563eb;

.crm-column {
  flex: 1;
  min-width: 290px;
  max-width: 320px;
  background-color: $slate-100;
  border: 1px solid $slate-200;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 210px);
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
  transition: all 0.2s ease;

  &--collapsed {
    max-width: 60px;
    min-width: 60px;
    overflow: hidden;
  }

  &--wip-exceeded {
    border-color: rgba($danger, 0.4);
    background-color: rgba($danger, 0.02);
  }

  &__header {
    padding: 0.9rem 1rem;
    border-bottom: 1px solid $slate-200;
    background-color: #ffffff;
    border-radius: 12px 12px 0 0;
  }

  &__title {
    font-size: 0.85rem;
    font-weight: 800;
    color: $slate-700;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 0.5rem;
    flex-shrink: 0;
  }

  &__count {
    font-size: 0.75rem;
    font-weight: 800;
    background-color: $slate-200;
    color: $slate-500;
    padding: 0.1rem 0.45rem;
    border-radius: 10px;

    &--warning {
      background-color: rgba($danger, 0.15);
      color: $danger;
    }
  }

  &__toggle {
    border: none;
    background: transparent;
    color: $slate-400;
    padding: 0.15rem;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: $slate-100;
      color: $slate-700;
    }
  }

  &__sub {
    font-size: 0.73rem;
    font-weight: 700;
    color: $slate-400;
    margin-top: 0.2rem;
  }

  &__cards {
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    overflow-y: auto;
    flex-grow: 1;
    min-height: 120px;
  }

  &__unpriced {
    color: #1e40af;
    font-weight: 700;
  }

  &__more {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;
    padding: 0.6rem 0.5rem 0.4rem;
  }

  &__more-label {
    font-size: 0.7rem;
    font-weight: 600;
    color: $slate-400;
  }

  &__load-more {
    width: 100%;
    border: 1px dashed $slate-200;
    background: #ffffff;
    color: $slate-500;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.4rem 0.5rem;
    border-radius: 8px;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: $slate-400;
      color: $slate-700;
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2.5rem 1rem;
    color: $slate-400;
    font-size: 0.75rem;
    font-weight: 600;
    border: 2px dashed $slate-200;
    border-radius: 8px;
    margin-top: 0.5rem;
  }
}

// Dragging states
.crm-card--ghost {
  opacity: 0.4;
  border: 2px dashed #2563eb;
}

.crm-card--dragging {
  transform: rotate(2deg) scale(1.02);
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
}

.scrollable-y {
  overflow-y: auto;
}
</style>
